/**
 * The route guard, and the "not yet authorised" state.
 *
 * **This is the instruction `lib/api/auth.ts` left for this rewrite**, quoted from its own
 * doc comment: a 403 from `require_allowed_user` must render the not-yet-authorised state
 * rather than a generic failure or a retry prompt — *"retrying will never succeed until an
 * admin allowlists the account"*. A spinner-and-retry there is not a cosmetic mistake; it
 * sends a user round a loop that cannot terminate.
 *
 * The guard itself is navigational only. Every route the app can reach is enforced server-side
 * by the dependency in `app/api/deps.py`, so what this does is decide what to *show* — and
 * being wrong about that reveals a page shell, never data.
 */

import { Alert, AlertTitle, Box, CircularProgress, Typography } from "@mui/material";
import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { ApiClientError } from "@/api/client";
import { useAuth } from "@/auth/AuthProvider";

export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Rendering the sign-in redirect before auth settles would bounce a signed-in user to the
  // sign-in page on every hard refresh.
  if (loading) return <CentredSpinner label="Checking your sign-in…" />;

  if (!user) {
    // `state.from` so sign-in can return the user to where they were heading, and `replace` so
    // the back button does not land them on a page they were just bounced off.
    return <Navigate to="/sign-in" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}

export function CentredSpinner({ label }: { label?: string }) {
  return (
    <Box className="flex min-h-[40vh] flex-col items-center justify-center gap-3">
      <CircularProgress />
      {label ? (
        <Typography variant="body2" className="text-white/80 text-on-video">
          {label}
        </Typography>
      ) : null}
    </Box>
  );
}

/**
 * The card a signed-in but unauthorised caller sees.
 *
 * Deliberately has **no retry control**. The only thing that changes this state is an admin
 * adding the account, which is why the copy names the action the user can actually take.
 */
export function NotAuthorisedNotice({ message }: { message?: string }) {
  return (
    <Box className="mx-auto max-w-md py-12">
      <Alert severity="info" variant="outlined" sx={{ backgroundColor: "rgba(255,255,255,0.9)" }}>
        <AlertTitle>You&rsquo;re signed in, but not yet authorised</AlertTitle>
        <Typography variant="body2">
          {message ??
            "Your account hasn't been authorised yet. Please contact your Jones + Palmer contact."}
        </Typography>
      </Alert>
    </Box>
  );
}

/**
 * Renders the right thing for a failed query.
 *
 * One place decides, so no page can accidentally present a 403 as a retryable error. Anything
 * that is not a 403 is a genuine failure and says so plainly, without echoing a body the API
 * did not intend for display — the client has already decided what is safe to show.
 */
export function QueryError({ error }: { error: unknown }) {
  if (error instanceof ApiClientError && error.isNotAuthorised) {
    return <NotAuthorisedNotice message={error.message} />;
  }

  const message =
    error instanceof Error ? error.message : "Something went wrong. Please try again.";

  return (
    <Box className="mx-auto max-w-md py-12">
      <Alert severity="error" variant="outlined" sx={{ backgroundColor: "rgba(255,255,255,0.9)" }}>
        <AlertTitle>That didn&rsquo;t work</AlertTitle>
        <Typography variant="body2">{message}</Typography>
      </Alert>
    </Box>
  );
}
